import React, { useCallback, useEffect } from 'react'
import { navigate } from 'gatsby'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import cx from 'classnames'
import { PRIMARY_COLOR_MAPPING } from '../../utils/constants'
import { BLUE_BASE, PINK } from '../../theme/variables/colors'
import { hexToRgba } from '../../utils/functions'
import { useSideNavigationContext } from '../../utils/context/sideNavigationContext'

const SCROLL_DURATION = 250

const StickyContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'noSticky' && prop !== 'inSideNav',
})(({ theme, noSticky, inSideNav }) => ({
  display: 'flex',
  justifyContent: 'center',
  position: noSticky ? 'relative' : 'sticky',
  zIndex: 777,
  top: noSticky ? 3 : -15,
  marginBottom: inSideNav ? '40px' : 'unset',
  '& > div': {
    width: '100%',
  },
  [theme.breakpoints.down('md')]: {
    top: 0,
    marginTop: 0,
  },
}))

const ScrollContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    overflowX: 'auto',
    width: '100vw',
  },
}))

const ItemsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'inSideNav',
})(({ inSideNav, theme }) => ({
  display: 'flex',
  flexDirection: inSideNav ? 'column' : 'row',
  backgroundColor: inSideNav ? 'transparent' : '#ECF2F5',
  width: inSideNav ? '100%' : 'max-content',
  borderRadius: 24,
  padding: inSideNav ? '12.5px 17px' : 0,
  margin: `8px auto ${inSideNav ? '40px' : '16px'} auto`,
  border: inSideNav
    ? `1px solid ${hexToRgba(BLUE_BASE, 0.4)}`
    : '1px solid #DEDEDE',
  boxShadow: inSideNav ? '0px 4px 16px 4px rgba(9, 42, 72, 0.04)' : 'unset',
  [theme.breakpoints.down('lg')]: {
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    border: '0',
    boxShadow: 'none !important',
  },
}))

const Item = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'inSideNav' &&
    prop !== 'noUppercaseItems' &&
    prop !== 'selectedUnderlineColor' &&
    prop !== 'selected',
})(({
  theme,
  inSideNav,
  noUppercaseItems,
  selected,
  selectedUnderlineColor,
}) => {
  const borderDividerColor = hexToRgba(BLUE_BASE, 0.4)
  const underlineColors = { ...PRIMARY_COLOR_MAPPING, blue: '#1194d1' }

  return {
    width: inSideNav ? '100%' : 'auto',
    padding: inSideNav ? '25px 0' : '10px 30px',
    borderRadius: inSideNav ? 0 : '40px',
    fontFamily: 'Poppins',
    fontWeight: inSideNav ? 500 : 600,
    fontSize: '16px',
    letterSpacing: '0.6px',
    cursor: 'pointer',
    userSelect: 'none',
    textTransform: noUppercaseItems || inSideNav ? 'none' : 'uppercase',
    borderBottom: inSideNav ? `1px solid ${borderDividerColor}` : 0,
    '&:last-of-type': {
      borderBottom: inSideNav ? 0 : `1px solid ${borderDividerColor}`,
    },
    [theme.breakpoints.down('lg')]: {
      margin: inSideNav ? 0 : '0px 5px',
      borderBottom: 0,
      border: 0,
      ...(selected && {
        boxShadow: 'none !impooortant',
        '&::after': {
          left: 'calc(50% - 38px) !important',
        },
      }),
    },
    ...(selected && {
      position: 'relative',
      background: inSideNav ? 'transparent' : '#092A48',
      color: inSideNav ? BLUE_BASE : '#ffffff',
      boxShadow: inSideNav ? 'none' : '0px 4px 16px 1px rgba(9, 42, 72, 0.24)',
      fontWeight: inSideNav ? 800 : 600,
      '&::after': {
        content: inSideNav ? '""' : 'unset',
        position: 'absolute',
        bottom: 23,
        left: 0,
        width: 77,
        height: 3,
        backgroundColor: underlineColors[selectedUnderlineColor] ?? PINK,
      },
    }),
  }
})

const SectionNavbar = ({
  sections,
  actions,
  initialIndex = 0,
  leftAlign = false,
  noInitialSelection = false,
  className = '',
  noNavigation = false,
  noUppercaseItems = false,
  inSideNav = false,
  noSticky = false,
  handleOptionClick = () => {},
}) => {
  const { highlightedNavItem, setHighlightedNavItem } =
    useSideNavigationContext()

  useEffect(() => {
    if (!noInitialSelection && typeof setHighlightedNavItem === 'function') {
      setHighlightedNavItem(Number(initialIndex))
    }
  }, [initialIndex, noInitialSelection, setHighlightedNavItem])

  const smoothScrollTo = useCallback((targetPosition) => {
    if (typeof window === 'undefined') return

    const startPosition = window.pageYOffset
    const distance = targetPosition - startPosition
    const startTime = performance.now()

    const step = (currentTime) => {
      if (typeof window === 'undefined') return
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / SCROLL_DURATION, 1)
      const newPosition = startPosition + distance * progress
      window.scrollTo(0, newPosition)

      if (progress < 1) {
        window.requestAnimationFrame(step)
      }
    }

    window.requestAnimationFrame(step)
  }, [])

  const handleItemClick = useCallback(
    (section, index) => () => {
      if (!section.customOnClick) {
        if (actions && actions[index]) {
          actions[index](section)
        }

        if (!noNavigation) {
          if (section.path && typeof window !== 'undefined') {
            const pathWithAnchor = `${section.path}${section.anchor_target ?? ''}`
            navigate(pathWithAnchor)
          } else if (!section.path && typeof window !== 'undefined') {
            const el = document.querySelector(section.id)
            if (el) {
              const targetPosition =
                el.getBoundingClientRect().top + window.scrollY - 50

              smoothScrollTo(targetPosition)

              setTimeout(() => {
                if (typeof setHighlightedNavItem === 'function') {
                  setHighlightedNavItem(index)
                }
                const hashlessUrl = window.location.href.split('#')[0]
                const newUrl = section.id.startsWith('#')
                  ? `${hashlessUrl}${section.id}`
                  : hashlessUrl
                window.history.pushState({}, '', newUrl)
                handleOptionClick('menu')
              }, SCROLL_DURATION + 50)
            }
          } else if (typeof setHighlightedNavItem === 'function') {
            setHighlightedNavItem(index)
          }
        } else if (typeof setHighlightedNavItem === 'function') {
          setHighlightedNavItem(index)
        }
      } else {
        section.customOnClick()
      }
    },
    [
      actions,
      handleOptionClick,
      noNavigation,
      setHighlightedNavItem,
      smoothScrollTo,
    ]
  )

  const getItemsContainerSx = (leftAlign) => ({
    margin: leftAlign ? 0 : 'auto',
  })
  const itemsContainerSx = getItemsContainerSx(leftAlign)

  return (
    <StickyContainer
      className={cx({ [className]: !inSideNav })}
      noSticky={noSticky}
    >
      <ScrollContainer>
        <ItemsContainer inSideNav={inSideNav} sx={itemsContainerSx}>
          {sections?.map((section, index) => (
            <Item
              key={section.id || index}
              selected={index === highlightedNavItem}
              inSideNav={inSideNav}
              noUppercaseItems={noUppercaseItems}
              selectedUnderlineColor={
                section.underline_color?.nav_selected_color
              }
              onClick={handleItemClick(section, index)}
            >
              {section.name}
            </Item>
          ))}
        </ItemsContainer>
      </ScrollContainer>
    </StickyContainer>
  )
}

export default SectionNavbar
